import React, { createContext, useContext, useState, useEffect } from "react";

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }

  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    const savedCart = localStorage.getItem("zootel_cart");

    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        setCartItems(Array.isArray(parsedCart) ? parsedCart : []);
      } catch (error) {
        console.error("Error loading cart from localStorage:", error);
        setCartItems([]);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("zootel_cart", JSON.stringify(cartItems));
  }, [cartItems]);

  const normalizeCartItem = (item) => {
    const normalizedType = String(item?.type || "").toLowerCase();

    const isService =
      normalizedType === "service" ||
      normalizedType === "services" ||
      item?.advance_booking_days !== undefined ||
      item?.available_days !== undefined ||
      item?.assigned_employees !== undefined ||
      item?.buffer_time_before !== undefined ||
      item?.buffer_time_after !== undefined;

    const type = isService ? "service" : "product";

    return {
      ...item,
      id: item.id,
      service_id: type === "service" ? item.service_id || item.id : item.service_id,
      product_id: type === "product" ? item.product_id || item.id : item.product_id,
      company_id: item.company_id || item.companyId || item.companyID || "",
      type,
      name: item.name || item.title || "Untitled",
      title: item.title || item.name || "Untitled",
      company: item.company || item.company_name || item.provider || "",
      price: Number(item.price || 0),
      image: item.image || item.image_url || "",
      image_url: item.image_url || item.image || "",
      description: item.description || "",
      quantity: Number(item.quantity || 1),
    };
  };

  const addToCart = (item) => {
    const normalizedItem = normalizeCartItem(item);

    setCartItems((prev) => {
      const existingItem = prev.find(
        (cartItem) =>
          String(cartItem.id) === String(normalizedItem.id) &&
          String(cartItem.type) === String(normalizedItem.type),
      );

      if (existingItem) {
        return prev.map((cartItem) =>
          String(cartItem.id) === String(normalizedItem.id) &&
          String(cartItem.type) === String(normalizedItem.type)
            ? {
                ...cartItem,
                quantity: Number(cartItem.quantity || 1) + 1,
              }
            : cartItem,
        );
      }

      return [...prev, normalizedItem];
    });
  };

  const removeFromCart = (itemId, itemType) => {
    setCartItems((prev) =>
      prev.filter((item) => {
        const sameId = String(item.id) === String(itemId);

        if (!itemType) {
          return !sameId;
        }

        return !(sameId && String(item.type) === String(itemType));
      }),
    );
  };

  const updateQuantity = (itemId, itemTypeOrQuantity, maybeQuantity) => {
    const hasType = maybeQuantity !== undefined;
    const itemType = hasType ? itemTypeOrQuantity : null;
    const quantity = hasType ? maybeQuantity : itemTypeOrQuantity;

    if (Number(quantity) <= 0) {
      removeFromCart(itemId, itemType);
      return;
    }

    setCartItems((prev) =>
      prev.map((item) => {
        const sameId = String(item.id) === String(itemId);
        const sameType = itemType ? String(item.type) === String(itemType) : true;

        if (sameId && sameType) {
          return {
            ...item,
            quantity: Number(quantity),
          };
        }

        return item;
      }),
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      return total + Number(item.price || 0) * Number(item.quantity || 1);
    }, 0);
  };

  const getCartItemsCount = () => {
    return cartItems.reduce((count, item) => {
      return count + Number(item.quantity || 1);
    }, 0);
  };

  const value = {
    cartItems,
    items: cartItems,

    addToCart,
    addItem: addToCart,

    removeFromCart,
    removeItem: removeFromCart,

    updateQuantity,
    clearCart,

    getCartTotal,
    getTotalPrice: getCartTotal,

    getCartItemsCount,
    getTotalItems: getCartItemsCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};